import { useState, useRef, useEffect, useCallback } from "react";
import { createUseStyles } from "react-jss";
import { mergeRefs } from "react-merge-refs";
import produce from "immer";
import { useDrag } from "react-dnd";
import { useHistory } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";

import { useTextSelector } from "./refresh/selectors/TextSelector";
import useHover from "../../lib/player/src/hooks/useHover";
import Preview from "../screens/Preview";
import AltImage from "../components/refresh/AltImage";
import { useImageSelector } from "./refresh/selectors/ImageSelector";
import { getStartImageId } from "./studio/sceneStatus";

const canImgBorder = 10;
const delta = 0.025 / 2; // 1 / 4 / 10 / 2
const delta2 = (1 / 4 - delta * 4) / 2 / 2;
const imgWidth = 200;
const imgHeight = 182;

// a react compontent that renders name in a <p>
const ImageName = ({ name, onChange }) => {
  const [value, getValue] = useTextSelector(name);
  const classes = useStyles({});

  useEffect(() => {
    if (value !== name) onChange(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, name]);

  return (
    <p
      className={classes.name}
      onDoubleClick={() =>
        getValue({ label: "Image name", value, name: "Edit Image Name" })
      }
    >
      {value}
    </p>
  );
};

const TourCanvasImage = ({
  tour,
  image,
  position,
  onUpdateImage,
  handleNodeDrawing,
  scale,
  active,
  selected,
  onUpdateActiveImage,
  onDeleteImage,
  onAddHotSpot,
  handleSetTargetImageInfo,
  clearNodeLink,
  isNodeDrawing,
  showHotSpot,
  onUpdateBoomerang,
  activeImage,
  targetImageInfo,
  onEmbedTour,
  setCanvasImageSize,
  alts,
}) => {
  const [ratio, setRatio] = useState(0);
  const [rotate, setRotate] = useState(0);
  const [hoverRef, hovering] = useHover();
  const [isHover, setIsHover] = useState(false);
  const [isActiveHover, setIsActiveHover] = useState(false);
  const [altMode, setAltMode] = useState(false);
  const [isInactiveHover, setIsInactiveHover] = useState(false);
  const [prevOffset, setPrevOffset] = useState({ width: null, height: null });
  const [tag, getTag] = useTextSelector(image.tag);
  const [newImage, getNewImage, setNewImage] = useImageSelector(null);
  const [{ isDragging }, drag, dragPreview] = useDrag({
    type: "image",
    item: { id: image.id, image },
    collect: monitor => ({ isDragging: monitor.isDragging() }),
  });
  const history = useHistory();
  const isStart = getStartImageId(tour) === image.id;
  const classes = useStyles({
    isDragging,
    url: `${image.urls.full}&w=200&h=200&q=60&auto=format&fit=crop`,
    hover: (hovering && !isNodeDrawing) || active,
    pointerEvents: isNodeDrawing ? "none" : "auto",
  });
  const ref = useRef();

  useEffect(() => {
    if (!active && !hovering) {
      setAltMode(false);
    }
  }, [active, hovering]);

  const onDelete = useCallback(
    id => {
      onUpdateImage(
        produce(image, draft => {
          draft.alts = draft.alts.filter(alt => alt !== id);
        }),
      );
    },
    [onUpdateImage, image],
  );

  useEffect(() => {
    if (newImage) {
      onUpdateImage(
        produce(image, draft => {
          draft.alts ??= [];
          if (!draft.alts.includes(newImage)) {
            draft.alts.push(newImage);
          }
        }),
      );
      setNewImage(null);
    }
  }, [newImage, onUpdateImage, image, setNewImage]);

  useEffect(() => {
    if (tag !== image.tag) {
      onUpdateImage(
        produce(image, draft => {
          draft.tag = tag;
        }),
      );
    }
  }, [tag, onUpdateImage, image]);

  useEffect(() => {
    const offset = getOffset();
    if (!offset) return;

    if (
      prevOffset.width !== offset.width ||
      prevOffset.height !== offset.height
    ) {
      const value = {
        width: offset.width / scale,
        height: offset.height / scale,
      };

      if (active) {
        value.height -= canImgBorder * scale;
      }
      setCanvasImageSize(value);
      setPrevOffset(offset);
    }
  }, [prevOffset.width, prevOffset.height, active, scale, setCanvasImageSize]);

  const onNameChange = name => {
    onUpdateImage(
      produce(image, draft => {
        draft.name = name;
      }),
    );
  };

  const handleMouseDown = e => {
    if (isActiveHover) {
      handleNodeDrawing(true);
    }
  };

  const handleMouseUp = e => {
    if (isNodeDrawing && isHover && !active) {
      onAddHotSpot(true);

      const value = {
        id: image.id,
        ratio: ratio,
      };

      handleSetTargetImageInfo(value);
    } else if (!showHotSpot) {
      clearNodeLink();
    }
    handleNodeDrawing(false);
  };

  const handleMouseOut = () => {
    setIsActiveHover(false);
    setIsInactiveHover(false);
  };

  const handleMouseMove = e => {
    const curPosX = e.clientX;
    const curPosY = e.clientY;
    let ratio = 0;
    const real_canImgBorder = canImgBorder * scale;
    let isHover = false;
    const offset = getOffset();
    setIsActiveHover(false);
    setIsInactiveHover(false);
    if (
      !(
        curPosX >= offset.x + real_canImgBorder &&
        curPosX <= offset.x + offset.width - real_canImgBorder &&
        curPosY >= offset.y + real_canImgBorder &&
        curPosY <= offset.y + offset.height - real_canImgBorder + 2
      )
    ) {
      // const width = offset.width - real_canImgBorder * 2;
      // const height = offset.height - real_canImgBorder * 2;

      if (curPosY < offset.y + real_canImgBorder) {
        ratio =
          (curPosX - offset.x - real_canImgBorder) /
          (offset.width - 2 * real_canImgBorder) /
          4;
      }
      if (curPosY > offset.y + offset.height - real_canImgBorder) {
        ratio =
          0.5 +
          (offset.x + offset.width - real_canImgBorder - curPosX) /
            (offset.width - 2 * real_canImgBorder) /
            4;
      }
      if (curPosX < offset.x + real_canImgBorder) {
        ratio =
          0.75 +
          (offset.y + offset.height - real_canImgBorder - curPosY) /
            (offset.height - 2 * real_canImgBorder) /
            4;
      }
      if (curPosX > offset.x + offset.width - real_canImgBorder) {
        ratio =
          0.25 +
          (curPosY - offset.y - real_canImgBorder) /
            (offset.height - 2 * real_canImgBorder) /
            4;
      }
      if (
        curPosX - offset.x <= real_canImgBorder &&
        curPosY - offset.y <= real_canImgBorder
      ) {
        ratio = 0;
      } else if (
        curPosX >= offset.x + offset.width - real_canImgBorder &&
        curPosY - offset.y <= real_canImgBorder
      ) {
        ratio = 0.25;
      } else if (
        curPosX >= offset.x + offset.width - real_canImgBorder &&
        curPosY >= offset.y + offset.height - real_canImgBorder
      ) {
        ratio = 0.5;
      } else if (
        curPosX - offset.x <= real_canImgBorder &&
        curPosY >= offset.y + offset.height - real_canImgBorder
      ) {
        ratio = 0.75;
      }
      ratio -= 0.125;
      if (ratio < 0) {
        ratio += 1;
      }
      isHover = !showHotSpot;
    }
    if (isHover) {
      if (active) setIsActiveHover(true);
      setRatio(ratio);
      if (isNodeDrawing) {
        setIsInactiveHover(true);
      } else {
        const rotate = getRotate(ratio);
        setRotate(rotate);
      }
    }
    setIsHover(isHover);
  };

  const getOffset = () => {
    return ref.current.getBoundingClientRect();
  };

  const getRotate = ratio => {
    const snapping_value = [];

    for (var j = 0; j <= 8; j++) {
      snapping_value.push((1 / 8) * j);
    }

    let rotate = 0;
    for (var i = 0; i <= 8; i++) {
      if (Math.abs(snapping_value[i] - ratio) < delta) {
        // in case of snapping region
        rotate = snapping_value[i];
        break;
      } else if (
        Math.abs(snapping_value[i] + delta + delta2 - ratio) < delta2
      ) {
        // in case of other region
        rotate =
          (ratio - snapping_value[i] - delta) / 2 / delta2 / 8 +
          snapping_value[i];
        break;
      }
    }

    return rotate;
  };

  let left = 0;
  let top = 0;

  if (position) {
    left = position.left;
    top = position.top;
  }

  const dist1 = (imgWidth - canImgBorder * 2) * delta * 4;
  // const dist2 = (imgHeight - canImgBorder * 2) * delta * 4;
  const width = (imgWidth - canImgBorder * 2) * delta2 * 2 * 4;
  const height = (imgHeight - canImgBorder * 2) * delta2 * 2 * 4;

  // const distX = canImgBorder + dist1;
  // const distY = canImgBorder + dist2;

  return (
    <div
      className={clsx(classes.image, {
        [classes.active]: active,
        [classes.selected]: selected && !active,
        [classes.inactiveHover]: isInactiveHover,
      })}
      style={{ left, top, transform: `scale(${scale})` }}
      ref={mergeRefs([dragPreview, ref])}
      onMouseMove={e => {
        handleMouseMove(e, image, scale);
      }}
      onMouseOut={() => {
        handleMouseOut();
      }}
      onMouseDown={e => {
        active && handleMouseDown(e);
      }}
      onMouseUp={e => {
        handleMouseUp(e);
      }}
    >
      {isStart && <div className={classes.startBadge}>START</div>}
      {active && (
        <div>
          <div
            className={classes.pattern}
            style={{
              left: dist1,
              top: -canImgBorder,
              width,
              height: canImgBorder,
            }}
          />
          <div
            className={classes.pattern}
            style={{
              right: dist1,
              top: -canImgBorder,
              width,
              height: canImgBorder,
            }}
          />
          <div
            className={classes.pattern}
            style={{
              left: dist1,
              bottom: -canImgBorder,
              width,
              height: canImgBorder,
            }}
          />
          <div
            className={classes.pattern}
            style={{
              right: dist1,
              bottom: -canImgBorder,
              width,
              height: canImgBorder,
            }}
          />

          <div
            className={classes.pattern}
            style={{
              left: -canImgBorder,
              top: dist1,
              width: canImgBorder,
              height,
            }}
          />
          <div
            className={classes.pattern}
            style={{
              right: -canImgBorder,
              bottom: dist1,
              width: canImgBorder,
              height,
            }}
          />
          <div
            className={classes.pattern}
            style={{
              right: -canImgBorder,
              top: dist1,
              width: canImgBorder,
              height,
            }}
          />
          <div
            className={classes.pattern}
            style={{
              left: -canImgBorder,
              bottom: dist1,
              width: canImgBorder,
              height,
            }}
          />
        </div>
      )}
      {active ? (
        <Preview
          tour={tour}
          onUpdateImage={onUpdateImage}
          onUpdateBoomerang={onUpdateBoomerang}
          onUpdateActiveImage={onUpdateActiveImage}
          activeImage={activeImage}
          onEmbedTour={onEmbedTour}
          rotate={rotate}
          show={isActiveHover}
          showHotSpot={showHotSpot}
          handleShowHotSpot={onAddHotSpot}
          nodeLinkInfo={{
            ratio: ratio,
            target: targetImageInfo,
          }}
          clearNodeLink={clearNodeLink}
          position={position}
        />
      ) : null}
      <div
        onClick={() => onUpdateActiveImage(image.id)}
        className={classes.filter}
        ref={hoverRef}
      >
        <div className={classes.content}>
          {altMode ? (
            <div className={classes.altContainer}>
              <div className={classes.alts}>
                <AltImage
                  image={image}
                  onOpen={() => {
                    onUpdateActiveImage(image.id);
                    setTimeout(() =>
                      history.push(`${history.location.pathname}/360`),
                    );
                  }}
                />
                <hr />
                {alts.length > 0
                  ? alts.map(alt => (
                      <AltImage
                        key={alt.id}
                        image={alt}
                        onDelete={onDelete}
                        onOpen={() => {
                          onUpdateActiveImage(alt.id);
                          setTimeout(() =>
                            history.push(`${history.location.pathname}/360`),
                          );
                        }}
                      />
                    ))
                  : null}
              </div>
              <div className={classes.row}>
                <img
                  src={`${window.location.origin}/tour/assets/hotspots/timeline.png`}
                  alt="Switch back from timeline mode"
                  onClick={() => setAltMode(false)}
                />
                <p
                  className={classes.addBtn}
                  onClick={() => getNewImage(image.id)}
                >
                  Add Alt Image
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className={classes.transparent}>
                <div className={classes.header}>
                  <div className={classes.handle} ref={drag}>
                    <FontAwesomeIcon
                      icon={["fal", "arrows"]}
                      className={classes.arrows}
                      fixedWidth
                    />
                  </div>
                  <ImageName name={image.name} onChange={onNameChange} />
                  <div
                    className={clsx(classes.setStart, {
                      [classes.setStartActive]: isStart,
                    })}
                    title={isStart ? "Start scene" : "Set as start scene"}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!isStart) onUpdateImage(image, { setAsStart: true });
                    }}
                  >
                    <FontAwesomeIcon
                      icon={["fal", "flag"]}
                      className={classes.setStartIcon}
                      fixedWidth
                    />
                  </div>
                  <div
                    className={classes.delete}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (
                        window.confirm(
                          "Are you sure you want to delete this image?",
                        )
                      ) {
                        try {
                          onDeleteImage(image);
                          clearNodeLink();
                          onAddHotSpot(false);
                        } catch (err) {
                          if (err?.message) alert(`Error: ${err.message}`);
                          else alert(`Error: ${err}`);
                        }
                      }
                    }}
                  >
                    <FontAwesomeIcon
                      icon={["fal", "trash-alt"]}
                      className={classes.deleteIcon}
                      fixedWidth
                    />
                  </div>
                </div>
                <button
                  onClick={() => {
                    onUpdateActiveImage(image.id);
                    setTimeout(() =>
                      history.push(`${history.location.pathname}/360`),
                    );
                  }}
                  className={classes.button}
                >
                  Open
                </button>
              </div>
              <div className={classes.grow} />
              <div className={classes.row}>
                <img
                  src={`${window.location.origin}/tour/assets/hotspots/timeline.png`}
                  alt="Switch to timeline mode"
                  onClick={() => setAltMode(true)}
                />
                <p
                  className={classes.tag}
                  onDoubleClick={() =>
                    getTag({
                      label: "Image Tag",
                      value: image.tag ?? "Untagged",
                      name: "Edit Tag",
                    })
                  }
                >
                  {image.tag ?? (hovering || active ? "Untagged" : "")}
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const useStyles = createUseStyles(theme => ({
  image: {
    opacity: props => (props.isDragging ? 0 : 1),
    position: "absolute",
    width: `${imgWidth}px`,
    height: `${imgHeight}px`,
    border: `1px solid ${theme.colorGrey}`,
    marginBottom: theme.spacing2,
    backgroundColor: theme.colorBackground,
    backgroundImage: props => `url("${props.url}")`,
    borderRadius: 3,
    backgroundSize: props => (props.hover ? "auto 130%" : "auto 120%"),
    backgroundPosition: "center",
    boxSizing: "border-box",
    lineHeight: 0,
    cursor: "auto",
    zIndex: 1,
    transitionProperty: "border-color, border-width, background-size",
    transition: "0.2s ease",
    userSelect: "none",
    transformOrigin: "top left",

    "& img": {
      borderRadius: theme.borderRadius,
      width: "100%",
    },

    "&:hover": {
      zIndex: 3,
    },
  },
  filter: {
    display: "flex",
    backgroundColor: props =>
      props.hover ? "rgba(0, 0, 0, 0.8)" : "rgba(0, 0, 0, 0.4)",
    width: "100%",
    height: "100%",
    borderRadius: 3,
    overflow: "hidden",
    transition: "background-color 0.2s ease",
    pointerEvents: props => props.pointerEvents,
  },
  content: {
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    margin: "auto",
    width: 180,
    height: 162,
    padding: 5,
  },
  transparent: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    position: "relative",
    alignItems: "center",
    "& *": {
      transition: "opacity 0.2s ease",
    },
    "& svg, button": {
      opacity: props => (props.hover ? 1 : 0),
    },
    "& p": {
      opacity: props => (props.hover ? 1 : 0.6),
    },
  },
  active: {
    zIndex: 2,
    borderColor: theme.colorPrimary,
    borderWidth: `${canImgBorder}px`,
  },
  inactiveHover: {
    zIndex: 2,
    borderColor: theme.colorLightGrey,
    borderWidth: `${canImgBorder}px`,
  },
  // Marquee multi-select highlight (WP-L). Uses an outline (not a border) so it
  // reads differently from the thick `active` border and doesn't shift layout.
  selected: {
    zIndex: 2,
    outline: `3px solid ${theme.colorAlternative}`,
    outlineOffset: 2,
  },
  header: {
    display: "flex",
  },
  handle: {
    cursor: "move",
  },
  arrows: {
    color: theme.colorPrimary,
    position: "absolute",
    left: 0,
  },
  delete: {
    cursor: "pointer",
    position: "absolute",
    right: 0,
  },
  deleteIcon: {
    color: theme.colorSecondary,
  },
  setStart: {
    cursor: "pointer",
    position: "absolute",
    right: 22,
  },
  setStartActive: {
    cursor: "default",
  },
  setStartIcon: {
    color: theme.colorLightGrey,
    "$setStartActive &": {
      color: theme.colorAlternative,
    },
  },
  startBadge: {
    position: "absolute",
    top: 4,
    right: 4,
    zIndex: 10,
    pointerEvents: "none",
    backgroundColor: theme.colorAlternative,
    color: theme.textColor,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.06em",
    lineHeight: 1,
    padding: "3px 6px",
    borderRadius: 3,
  },
  button: {
    fontSize: 12,
    color: "white",
    backgroundColor: theme.colorRed,
    borderRadius: 25,
    border: "none",
    opacity: 0.9,
    cursor: "pointer",
    width: 50,
    height: 50,
    top: 50,
    position: "absolute",
  },
  pattern: {
    position: "absolute",
    backgroundColor: "#079baa",
    opacity: 0,
    animation: "$fadeIn 0.5s ease forwards 0.3s",
  },
  "@keyframes fadeIn": {
    from: {
      opacity: 0,
    },
    to: {
      opacity: 1,
    },
  },
  grow: {
    flexGrow: 1,
  },
  name: {
    textAlign: "center",
    color: theme.textColor,
    lineHeight: "1rem",
    cursor: "pointer",
    margin: 0,
    maxWidth: 135,
  },
  tag: {
    color: theme.textColor,
    textAlign: "right",
    lineHeight: "initial",
    cursor: "pointer",
    margin: 0,
  },
  row: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    "& img": {
      opacity: props => (props.hover ? 1 : 0),
      transition: "opacity 0.1s ease",
      width: 20,
      height: 20,
      cursor: "pointer",
    },
  },
  altContainer: {
    display: "flex",
    flexDirection: "column",
  },
  alts: {
    height: 132,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",

    "& hr": {
      width: "calc(100% - 2px)",
    },
  },
  addBtn: {
    color: "white",
    cursor: "pointer",
    lineHeight: "initial",
    margin: 0,

    "&:hover": {
      textDecoration: "underline",
    },
  },
}));

export default TourCanvasImage;
